import {DocumentStoreApi} from '@/components/document/store';
import {
  FORM_FIELD_ALLOCATION_SELECTED,
  USER_PROFILE_ALLOCATIONS,
  FORM_FIELD_ALLOCATION_FEES,
  FORM_FIELD_ALLOCATION_DEPOSIT_TYPE,
  FORM_FIELD_DEPOSIT_TYPE_MAP,
  FORM_FIELD_ALLOCATIONS_AMOUNT_PERIODIC,
  FORM_FIELD_ALLOCATIONS_AMOUNT_CASH_COMPUTED,
  FORM_FIELD_ALLOCATIONS_AMOUNT_EFT_COMPUTED,
  FORM_FIELD_ALLOCATIONS_AMOUNT_TRANSFER,
  COMPUTED_ALLOCATION_ACCOUNTS,
} from '@/constants/engine';
import {ValueResolver} from '@/engine/types';
import {logger} from '@/initializers/logger';
import {Reference} from '@/model/types';
import {Allocation} from '@/repositories/beneva-api/endpoints/allocations';

export const getAllocationFormValues = (store: DocumentStoreApi, valueResolver: ValueResolver) => {
  // Get the selected allocation's id
  const selectedAllocationFieldValue = store.getState().data[FORM_FIELD_ALLOCATION_SELECTED];
  const selectedAllocationRef =
    !Array.isArray(selectedAllocationFieldValue) &&
    typeof selectedAllocationFieldValue !== 'string' &&
    '$ref' in selectedAllocationFieldValue
      ? selectedAllocationFieldValue.$ref
      : null;
  const selectedAllocationId =
    selectedAllocationRef && selectedAllocationRef.startsWith(`${USER_PROFILE_ALLOCATIONS}_`)
      ? selectedAllocationRef.split('_').pop()
      : selectedAllocationRef;
  if (!selectedAllocationId) {
    logger.error('Could not get the selected allocation id');
    return undefined;
  }

  // Get the other form values
  const fees = valueResolver<Reference>({$ref: FORM_FIELD_ALLOCATION_FEES}, {keepDomainReference: true});
  const depositType = valueResolver({$ref: FORM_FIELD_ALLOCATION_DEPOSIT_TYPE});
  if (!FORM_FIELD_DEPOSIT_TYPE_MAP.has(depositType)) {
    logger.error('Could not get the deposit type');
    return undefined;
  }

  // Get the investment amounts
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const totalInvestment = Number(valueResolver({$ref: FORM_FIELD_DEPOSIT_TYPE_MAP.get(depositType)!}));
  const isPeriodicInvestment = Number(valueResolver({$ref: FORM_FIELD_ALLOCATIONS_AMOUNT_PERIODIC})) > 0;
  const isCashInvestment = Number(valueResolver({$ref: FORM_FIELD_ALLOCATIONS_AMOUNT_CASH_COMPUTED})) > 0;
  const isEFTInvestment = Number(valueResolver({$ref: FORM_FIELD_ALLOCATIONS_AMOUNT_EFT_COMPUTED})) > 0;
  const isTransferInvestment = Number(valueResolver({$ref: FORM_FIELD_ALLOCATIONS_AMOUNT_TRANSFER})) > 0;

  // Get the selected allocation's data from the advisor profile
  const allocationsJson = valueResolver({$ref: USER_PROFILE_ALLOCATIONS}, {keepDomainReference: true});
  const allocations = JSON.parse(allocationsJson) as (Omit<Allocation, 'accountType'> & {accountType: string})[];
  const selectedAllocation = allocations.find(allocation => allocation.id === selectedAllocationId);
  if (!selectedAllocation) {
    logger.error('Could not get the selected allocation');
    return undefined;
  }

  // Get the accounts count
  const accountsCount = Number(valueResolver({$ref: `_${COMPUTED_ALLOCATION_ACCOUNTS}[count]`}));

  return {
    selectedAllocation,
    fees,
    depositType,
    totalInvestment,
    isPeriodicInvestment,
    isCashInvestment,
    isEFTInvestment,
    isTransferInvestment,
    allocations,
    accountsCount,
  };
};
